import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AnimatedSection from "@/components/AnimatedSection";
import { MaterialIcon } from "@/components/ui/material-icon";

type StatusValue = "completed" | "in_progress" | "future";

interface GameDevProgress {
  id: string;
  game_name: string;
  code_name: string | null;
  game_id: string | null;
  design_status: StatusValue;
  art_status: StatusValue;
  development_status: StatusValue;
  blind_playtesting_status: StatusValue;
  solo_design_status: StatusValue;
  graphic_design_status: StatusValue;
  pre_production_status: StatusValue;
  components_status: StatusValue;
  print_assemble_status: StatusValue;
  freight_shipping_status: StatusValue;
  launch_fulfillment_status: StatusValue;
  retail_release_status: StatusValue;
  target_date: string | null;
  sort_order: number;
  games?: {
    id: string;
    title: string;
  } | null;
}

const STATUS_COLUMNS = [
  { key: "design_status", label: "Design", icon: "edit" },
  { key: "art_status", label: "Art", icon: "palette" },
  { key: "development_status", label: "Development", icon: "settings" },
  { key: "blind_playtesting_status", label: "Blind Playtesting", icon: "play_arrow" },
  { key: "graphic_design_status", label: "Graphic Design", icon: "design_services" },
  { key: "pre_production_status", label: "Pre-Production", icon: "assignment" },
  { key: "components_status", label: "Components", icon: "extension" },
  { key: "print_assemble_status", label: "Print & Assemble", icon: "print" },
  { key: "freight_shipping_status", label: "Freight Shipping", icon: "local_shipping" },
  { key: "launch_fulfillment_status", label: "Launch & Fulfillment", icon: "rocket_launch" },
  { key: "retail_release_status", label: "Retail Release", icon: "storefront" },
] as const;

function StatusCircle({ status }: { status: StatusValue }) {
  if (status === "completed") {
    return <div className="w-5 h-5 rounded-full bg-blood-red" />;
  }
  if (status === "in_progress") {
    return (
      <div className="w-5 h-5 rounded-full border-2 border-blood-red relative overflow-hidden">
        <div className="absolute inset-0 bg-blood-red" style={{ clipPath: "inset(0 50% 0 0)" }} />
      </div>
    );
  }
  return <div className="w-5 h-5 rounded-full border-2 border-blood-red" />;
}

export function GameDevProgressSection() {
  const { data: progressItems, isLoading } = useQuery({
    queryKey: ["game-dev-progress-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("game_dev_progress")
        .select("*, games(id, title)")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as GameDevProgress[];
    },
  });

  if (isLoading || !progressItems || progressItems.length === 0) {
    return null;
  }

  return (
    <section id="development-progress" className="bg-white relative overflow-hidden scroll-mt-24 py-28">
      <div className="container mx-auto">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection animation="fade-up" className="text-center mb-12">
            <span className="section-label">Game Status</span>
            <h2 className="section-title text-[hsl(0,0%,10%)]">Development Progress</h2>
          </AnimatedSection>

          <AnimatedSection animation="fade-up" delay={100}>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[hsl(0,0%,85%)]">
                    <th className="text-left py-3 px-4 font-medium text-[hsl(0,0%,40%)] text-sm">Game</th>
                    {STATUS_COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className="text-center py-3 px-2 font-medium text-[hsl(0,0%,40%)]"
                        title={col.label}
                      >
                        <MaterialIcon name={col.icon} size="sm" className="text-[hsl(0,0%,10%)]" />
                      </th>
                    ))}
                    <th className="text-center py-3 px-4 font-medium text-[hsl(0,0%,40%)] text-sm">Target</th>
                  </tr>
                </thead>
                <tbody>
                  {progressItems.map((item) => (
                    <tr key={item.id} className="border-b border-[hsl(0,0%,20%)] hover:bg-white/5 transition-colors">
                      <td className="py-4 px-4">
                        {item.game_id && item.games ? (
                          <Link
                            to={`/games/${item.games.title
                              .toLowerCase()
                              .replace(/\s+/g, "-")
                              .replace(/[^a-z0-9-]/g, "")}`}
                            className="group"
                          >
                            <div className="font-medium text-[hsl(0,0%,10%)] group-hover:text-primary transition-colors flex items-center gap-1">
                              {item.game_name}
                              <MaterialIcon
                                name="arrow_forward"
                                size="sm"
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                              />
                            </div>
                            {item.code_name && <div className="text-xs text-[hsl(0,0%,50%)]">({item.code_name})</div>}
                          </Link>
                        ) : (
                          <>
                            <div className="font-medium text-[hsl(0,0%,10%)]">{item.game_name}</div>
                            {item.code_name && <div className="text-xs text-[hsl(0,0%,50%)]">({item.code_name})</div>}
                          </>
                        )}
                      </td>
                      {STATUS_COLUMNS.map((col) => (
                        <td key={col.key} className="text-center py-4 px-2">
                          <div className="flex justify-center">
                            <StatusCircle status={item[col.key as keyof GameDevProgress] as StatusValue} />
                          </div>
                        </td>
                      ))}
                      <td className="text-center py-4 px-4 text-sm text-[hsl(0,0%,50%)]">{item.target_date || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Icon Key */}
            <div className="mt-16 p-6 border border-[hsl(0,0%,90%)] bg-[#0c0c0c]">
              <h3 className="text-sm font-semibold text-[hsl(0,0%,10%)] mb-4 text-center">Icon Key</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {STATUS_COLUMNS.map((col) => (
                  <div key={col.key} className="flex items-center gap-2 text-sm">
                    <MaterialIcon name={col.icon} size="sm" className="text-[hsl(0,0%,10%)]" />
                    <span className="text-[hsl(0,0%,40%)]">{col.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-8 mt-6 pt-4 border-t border-[hsl(0,0%,85%)]">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-blood-red" />
                  <span className="text-sm text-[hsl(0,0%,40%)]">Completed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full border-2 border-blood-red relative overflow-hidden">
                    <div className="absolute inset-0 bg-blood-red" style={{ clipPath: "inset(0 50% 0 0)" }} />
                  </div>
                  <span className="text-sm text-[hsl(0,0%,40%)]">In Progress</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full border-2 border-blood-red" />
                  <span className="text-sm text-[hsl(0,0%,40%)]">Future</span>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
